import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { validateQuery, executeRawQuery } from '../services/api';
import { substituteVars } from '../utils/sqlVariables';
import ResultsTable from './ResultsTable';
import SeriesColorPicker from './SeriesColorPicker';

const CHART_TYPES = [
  { value: 'table', label: 'Table' },
  { value: 'bar', label: 'Bar Chart' },
  { value: 'grouped', label: 'Grouped' },
  { value: 'stacked', label: 'Stacked' },
  { value: 'line', label: 'Line Chart' },
  { value: 'pie', label: 'Pie Chart' },
  { value: 'metric', label: 'Metric' },
];

function WidgetSettingsModal({ widget, isOpen, onClose, onSave, templateVars, sqlSetterRef, sqlRef }) {
  const [title, setTitle] = useState(widget?.title || '');
  const [sql, setSql] = useState(widget?.sql || '');
  const [chartType, setChartType] = useState(widget?.chartType || 'table');
  const [previewResults, setPreviewResults] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null);
  const [seriesColors, setSeriesColors] = useState(widget?.seriesColors || {});
  const [detectedSeries, setDetectedSeries] = useState([]);
  const [unit, setUnit] = useState(widget?.unit || '');

  useEffect(() => {
    if (sqlSetterRef) {
      sqlSetterRef.current = setSql;
      return () => { sqlSetterRef.current = null; };
    }
  }, [sqlSetterRef]);

  useEffect(() => {
    if (sqlRef) {
      sqlRef.current = sql;
    }
  }, [sql, sqlRef]);

  useEffect(() => {
    if (isOpen && widget?.sql) {
      setPreviewLoading(true);
      setPreviewError(null);
      executeRawQuery(substituteVars(widget.sql, templateVars))
        .then(data => setPreviewResults(data))
        .catch(err => setPreviewError(err.message || 'Query failed'))
        .finally(() => setPreviewLoading(false));
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const handleValidate = async () => {
    try {
      const result = await validateQuery(sql);
      setValidationStatus(result);
    } catch (err) {
      setValidationStatus({ is_valid: false, issues: [{ message: err.message }] });
    }
  };

  const handleRunQuery = async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const data = await executeRawQuery(substituteVars(sql, templateVars));
      setPreviewResults(data);
    } catch (err) {
      setPreviewError(err.message || 'Query failed');
      setPreviewResults(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = () => {
    onSave({ id: widget.id, title, sql, chartType, seriesColors, unit: unit.trim() || null });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Widget Settings</h3>
          <button className="modal-close-btn" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="modal-field">
            <label>Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Widget title"
            />
          </div>

          <div className="modal-field">
            <label>Chart Type</label>
            <div className="view-toggle">
              {CHART_TYPES.map(ct => (
                <button
                  key={ct.value}
                  className={chartType === ct.value ? 'active' : ''}
                  onClick={() => setChartType(ct.value)}
                >
                  {ct.label}
                </button>
              ))}
            </div>
          </div>

          {chartType === 'metric' && (
            <div className="modal-field">
              <label>Unit (e.g. sec, hrs, PRs)</label>
              <input
                type="text"
                value={unit}
                onChange={e => setUnit(e.target.value)}
                placeholder="Optional unit label"
                style={{ maxWidth: '200px' }}
              />
            </div>
          )}

          <div className="modal-field">
            <label>SQL Query</label>
            <div className="monaco-wrapper" style={{ height: '200px' }}>
              <Editor
                height="200px"
                language="sql"
                theme="vs"
                value={sql}
                onChange={v => setSql(v || '')}
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  tabSize: 2,
                  wordWrap: 'on',
                  fixedOverflowWidgets: true,
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <button onClick={handleValidate} className="modal-action-btn">Validate</button>
            <button onClick={handleRunQuery} disabled={previewLoading} className="modal-action-btn modal-action-btn-primary">
              {previewLoading ? 'Running...' : 'Run Query'}
            </button>
          </div>

          {validationStatus && (
            <div style={{
              padding: '0.5rem',
              marginBottom: '0.5rem',
              borderRadius: '6px',
              background: validationStatus.is_valid ? '#e8f5e9' : '#ffebee',
              color: validationStatus.is_valid ? '#2e7d32' : '#c62828',
              fontSize: '0.8rem'
            }}>
              {validationStatus.is_valid ? 'Query is valid' : validationStatus.issues?.map((issue, i) => <div key={i}>{issue.message}</div>)}
            </div>
          )}

          {previewError && <div style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '0.5rem' }}>{previewError}</div>}

          {previewResults && (
            <>
              <SeriesColorPicker seriesNames={detectedSeries} seriesColors={seriesColors} onChange={setSeriesColors} />
              <div style={chartType === 'metric'
                ? { height: '120px', position: 'relative', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }
                : { maxHeight: '250px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem' }
              }>
                <ResultsTable
                  key={chartType}
                  results={previewResults}
                  defaultViewMode={chartType}
                  hideViewToggle
                  seriesColors={seriesColors}
                  unit={unit}
                  onSeriesDetected={({ numericColumns, groupedSeries, stackedSeries }) => {
                    if (chartType === 'metric') setDetectedSeries(numericColumns);
                    else if (chartType === 'stacked' && stackedSeries.length > 0) setDetectedSeries(stackedSeries);
                    else if (chartType === 'grouped' && groupedSeries.length > 0) setDetectedSeries(groupedSeries);
                    else if (chartType === 'pie') setDetectedSeries(previewResults?.results?.slice(0, 10).map(r => Object.values(r).find(v => typeof v === 'string') || `Row`) || []);
                    else setDetectedSeries(numericColumns);
                  }}
                />
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="modal-action-btn">Cancel</button>
          <button onClick={handleSave} disabled={!title.trim() || !sql.trim()} className="modal-action-btn modal-action-btn-primary">Save</button>
        </div>
      </div>
    </div>
  );
}

export default WidgetSettingsModal;
